import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

const ReconToastContext = createContext(null);

export function ReconToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev.slice(-4), { id, type: 'success', ...toast }]);
    setTimeout(() => dismiss(id), toast.duration ?? 4200);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ReconToastContext.Provider value={api}>
      {children}
      <div className="recon-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'recon-toast',
              t.type === 'error' ? 'recon-toast-error' : 'recon-toast-success',
            )}
          >
            {t.type === 'error' ? (
              <XCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-bold">{t.title}</p>
              {t.message ? <p className="m-0 mt-0.5 text-xs opacity-85">{t.message}</p> : null}
            </div>
            <button type="button" className="recon-toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ReconToastContext.Provider>
  );
}

export function useReconToast() {
  const ctx = useContext(ReconToastContext);
  if (!ctx) throw new Error('useReconToast must be used within ReconToastProvider');
  return ctx;
}
